/**
 * Shared UI primitives for the dashboard (SectionTag, Pill, Highlight, etc.).
 */
import type { CSSProperties, ReactNode } from "react";

const SECTION_FONT = "var(--font-section)";
const MONO = "var(--font-mono)";

/** Section eyebrow: monospace index + tracked label (no colored rule).
 *  Pass `as="h2"` to render it as a semantic heading (used where the eyebrow is
 *  a section's real heading); the wrapper resets margin/weight/line-height/size
 *  so the global h2 (text-h2 / font-crake) rules never leak and it looks
 *  identical to the default <div>. */
export function SectionTag({ index, label, as }: { index: string; label: string; as?: "h2" }) {
  const Tag = as ?? "div";
  const wrapperStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 12,
    ...(as ? { margin: 0, fontSize: 11, fontWeight: 400, lineHeight: 1.2 } : null),
  };
  return (
    <Tag style={wrapperStyle}>
      <span style={{ fontFamily: MONO, fontSize: 12, color: "var(--co-muted)", letterSpacing: ".04em" }}>
        {index}
      </span>
      <span
        style={{
          fontFamily: SECTION_FONT,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: ".18em",
          textTransform: "uppercase",
          color: "var(--co-text)",
        }}
      >
        {label}
      </span>
    </Tag>
  );
}

/**
 * The Pill: an accent marker with a live dot, for news ("4 new since
 * Tuesday"), never a neutral tag. A label that only describes something is a
 * rectangular neutral tag; a pill for it would make a description read as an
 * alert. Radius 6 (library style B, 1 Oct 2026).
 *
 * `onMedia` sets it on an opaque ground for use over a video frame. The accent
 * tint alone is a tenth of a colour: over footage it has no contrast of its
 * own, and coloured ink on thin glass over video is the exact failure the
 * material rules name.
 */
export function Pill({
  children,
  onMedia = false,
}: {
  children: ReactNode;
  onMedia?: boolean;
}) {
  return (
    <span className={onMedia ? "co-pill co-pill--media" : "co-pill"}>
      <span className="co-pill-dot co-livedot" aria-hidden="true" />
      {children}
    </span>
  );
}

/** Recording tape overlay: live dot + REC + timecode (for the move teaser). */
export function RecordingTape({ timecode }: { timecode: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 9,
        background: "rgba(0,0,0,.55)",
        border: "1px solid rgba(255,255,255,.14)",
        padding: "5px 10px",
        fontFamily: MONO,
        fontSize: 11,
        color: "#f5f5f5",
        letterSpacing: ".04em",
        WebkitBackdropFilter: "blur(8px)",
        backdropFilter: "blur(8px)",
      }}
    >
      <span className="co-livedot" style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--co-alert)", boxShadow: "0 0 8px rgba(239,68,68,.7)", flex: "none" }} />
      <span style={{ fontWeight: 600, letterSpacing: ".16em" }}>REC</span>
      <span style={{ color: "rgba(245,245,245,.8)" }}>{timecode}</span>
    </span>
  );
}

/**
 * Highlight (CreativeOS Library.lib.pen, Components / Status and feedback,
 * style B): the wrapped cell's own 1px edge at radius 12, with no fill and no
 * pegs (pegs retired 1 Oct 2026). It replaces side-stripe borders and filled
 * callouts, one per view, pointing at the thing that matters now. Accepts
 * className/style so it can BE the surface it emphasizes (e.g. a card).
 */
export function Highlight({
  children,
  className,
  style,
  tone = "accent",
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * accent marks the current or waiting item; ink marks a fact the reader
   * should carry away, or a surface that already spends its accent elsewhere;
   * plain is the figure alone, with no edge.
   */
  tone?: "accent" | "ink" | "plain";
}) {
  return (
    <div
      className={className}
      style={{
        position: "relative",
        borderRadius: 12,
        ...(tone === "plain" ? null : { border: `1px solid var(${tone === "ink" ? "--co-text" : "--co-accent"})` }),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Donut gauge: lime arc to `value`%, center big label. */
export function Donut({ value, label, sublabel }: { value: number; label: string; sublabel?: string }) {
  const size = 168;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, value)) / 100;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--co-line2)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--co-accent)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(c * filled).toFixed(1)} ${c.toFixed(1)}`}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
        <div>
          <div style={{ fontFamily: SECTION_FONT, fontWeight: 800, fontSize: 34, lineHeight: 1, letterSpacing: "-.01em", color: "var(--co-text)" }}>
            {label}
          </div>
          {sublabel ? (
            <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--co-muted)", marginTop: 6 }}>{sublabel}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Shared pillar dot color resolver (badge dots only). */
export const PILLAR_COLOR: Record<string, string> = {
  strategy: "var(--co-pillar-strategy)",
  creative: "var(--co-pillar-creative)",
  production: "var(--co-pillar-production)",
  culture: "var(--co-pillar-culture)",
};
